import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import './ChartOfAccounts.css';

const ChartOfAccounts = () => {
  const { companyId } = useCompany();
  const queryClient = useQueryClient();
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    parentId: null,
    name: '',
    accountType: 'activo',
    nature: 'deudor',
    level: 1,
    allowsMovements: true
  });

  // Obtener plan de cuentas
  const { data, isLoading, error } = useQuery({
    queryKey: ['chartOfAccounts', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/chart-of-accounts`);
      return response.data.data;
    },
    enabled: !!companyId
  });

  // Crear cuenta
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/companies/${companyId}/chart-of-accounts`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['chartOfAccounts', companyId]);
      setShowForm(false);
      setFormData({
        code: '',
        parentId: null,
        name: '',
        accountType: 'activo',
        nature: 'deudor',
        level: 1,
        allowsMovements: true
      });
    }
  });

  // Renderizar árbol de cuentas
  const renderTree = (accounts, parentId = null, level = 0) => {
    const children = accounts.filter(acc => acc.parentId === parentId);
    
    if (children.length === 0) return null;

    return (
      <ul className={`account-tree level-${level}`}>
        {children.map(account => (
          <li key={account.id} className="account-item">
            <div 
              className="account-row"
              onClick={() => setSelectedAccount(account)}
            >
              <span className="account-code">{account.code}</span>
              <span className="account-name">{account.name}</span>
              <span className="account-type">{account.accountType}</span>
              <span className="account-nature">{account.nature}</span>
            </div>
            {renderTree(accounts, account.id, level + 1)}
          </li>
        ))}
      </ul>
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  if (isLoading) return <div>Cargando plan de cuentas...</div>;
  if (error) return <div>Error al cargar plan de cuentas</div>;

  return (
    <div className="chart-of-accounts">
      <div className="page-header">
        <h2>Plan de Cuentas (PUC)</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancelar' : '+ Nueva Cuenta'}
        </button>
      </div>

      {showForm && (
        <div className="account-form-card">
          <h3>Crear Nueva Cuenta</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Código</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nivel</label>
                <input
                  type="number"
                  min="1"
                  max="6"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Nombre</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Tipo de Cuenta</label>
                <select
                  value={formData.accountType}
                  onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                  required
                >
                  <option value="activo">Activo</option>
                  <option value="pasivo">Pasivo</option>
                  <option value="patrimonio">Patrimonio</option>
                  <option value="ingreso">Ingreso</option>
                  <option value="gasto">Gasto</option>
                </select>
              </div>
              <div className="form-group">
                <label>Naturaleza</label>
                <select
                  value={formData.nature}
                  onChange={(e) => setFormData({ ...formData, nature: e.target.value })}
                  required
                >
                  <option value="deudor">Deudor</option>
                  <option value="acreedor">Acreedor</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.allowsMovements}
                  onChange={(e) => setFormData({ ...formData, allowsMovements: e.target.checked })}
                />
                Permite movimientos
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isLoading}>
                {createMutation.isLoading ? 'Creando...' : 'Crear Cuenta'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="accounts-container">
        <div className="accounts-tree">
          <h3>Árbol de Cuentas</h3>
          {data?.accounts && data.accounts.length > 0 ? (
            renderTree(data.accounts)
          ) : (
            <div className="empty-state">
              <p>No hay cuentas registradas</p>
              <p>Importa el PUC estándar desde el backend o crea cuentas manualmente</p>
            </div>
          )}
        </div>

        {selectedAccount && (
          <div className="account-details">
            <h3>Detalle de Cuenta</h3>
            <div className="detail-item">
              <strong>Código:</strong> {selectedAccount.code}
            </div>
            <div className="detail-item">
              <strong>Nombre:</strong> {selectedAccount.name}
            </div>
            <div className="detail-item">
              <strong>Tipo:</strong> {selectedAccount.accountType}
            </div>
            <div className="detail-item">
              <strong>Naturaleza:</strong> {selectedAccount.nature}
            </div>
            <div className="detail-item">
              <strong>Nivel:</strong> {selectedAccount.level}
            </div>
            <div className="detail-item">
              <strong>Permite movimientos:</strong> {selectedAccount.allowsMovements ? 'Sí' : 'No'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartOfAccounts;

